/**
 * Normalized page lifecycle state machine.
 *
 * Chrome freezes backgrounded pages (Android WebAPK in particular) and, on the
 * way back to the foreground, does **not** always deliver `visibilitychange`.
 * Device traces show `resume` followed by `focus` being emitted while
 * `visibilitychange`, `pageshow`, and `pagehide` stay silent, which leaves any
 * `visibilitychange`-only consumer stuck in its paused state.
 *
 * This store observes every event that carries lifecycle information and
 * collapses them into a single state, so consumers react to real transitions
 * instead of guessing liveness.
 *
 * Ported from the reference implementation of PageLifecycle.js
 * (GoogleChromeLabs, Apache-2.0) to keep the project free of an untyped and
 * unmaintained dependency.
 *
 * @see https://developer.chrome.com/docs/web-platform/page-lifecycle-api
 * @see https://github.com/GoogleChromeLabs/page-lifecycle
 */

/**
 * Lifecycle states, ordered from most to least active.
 *
 * - `active`: visible and focused.
 * - `passive`: visible but not focused.
 * - `hidden`: not visible, still running.
 * - `frozen`: CPU suspended by the browser; timers and animation frames stop.
 * - `terminated`: unloaded; no further transition will happen.
 */
export type PageLifecycleState =
  | "active" |
  "passive" |
  "hidden" |
  "frozen" |
  "terminated";

/** A single state change, delivered to subscribers. */
export interface PageLifecycleTransition {

    /** State the page just left. */
    "previousState": PageLifecycleState;

    /** State the page just entered. */
    "nextState": PageLifecycleState;

    /** Whether {@link previousState} was a visible state. */
    "wasVisible": boolean;

    /** Whether {@link nextState} is a visible state. */
    "isVisible": boolean;
}

/** Immutable view of the current state, suitable for `useSyncExternalStore`. */
export interface PageLifecycleSnapshot {

    /** Current lifecycle state. */
    "state": PageLifecycleState;

    /** Whether {@link state} is a visible state. */
    "isVisible": boolean;
}

/** Subscriber signature. */
export type PageLifecycleListener = ( transition: PageLifecycleTransition ) => void;

/**
 * Every event that carries lifecycle information.
 *
 * They do not share a target (`pageshow` / `pagehide` / `focus` / `blur` fire on
 * `window`, while `visibilitychange` / `freeze` / `resume` fire on `document`)
 * and most of them do not bubble. Listening on `window` in the **capture**
 * phase is therefore the only way to observe all of them from a single target,
 * and it runs before any other code could stop propagation.
 */
const LIFECYCLE_EVENT_NAMES = [
    "focus",
    "blur",
    "visibilitychange",
    "freeze",
    "resume",
    "pageshow",
    "pagehide",
] as const;

/**
 * Tells whether a state means the page is on screen.
 *
 * @param state Lifecycle state to classify.
 * @returns `true` for `active` and `passive`, `false` otherwise.
 */
export const isVisibleLifecycleState = ( state: PageLifecycleState ): boolean => {
    return state === "active" || state === "passive";
};

/**
 * Observes page lifecycle transitions on a given window.
 *
 * Listeners are attached lazily on the first subscription and kept for the
 * lifetime of the store: re-attaching on every subscriber churn would risk
 * missing a `resume` that arrives while no consumer is mounted.
 *
 * @remarks Duplicate events are swallowed. Browsers emit bursts on refocus
 * (for instance `resume` then `focus` then `visibilitychange`), and consumers
 * must not restart polling or refetch snapshots several times per resume.
 */
export class PageLifecycleStore {

    /** Window whose lifecycle is observed. */
    private readonly target: Window;

    /** Active subscribers. */
    private readonly listeners = new Set<PageLifecycleListener>();

    /** Current lifecycle state. */
    private state: PageLifecycleState;

    /** Cached snapshot, replaced only on real transitions to stay referentially stable. */
    private snapshot: PageLifecycleSnapshot;

    /** Whether DOM listeners are currently attached. */
    private isListening: boolean = false;

    /**
     * Create a store bound to a window.
     *
     * @param target Window to observe. Defaults to the global `window`, and is
     * injectable so tests can drive a controlled event target.
     */
    public constructor( target: Window = window ) {
        this.target = target;
        this.state = this.readCurrentState();
        this.snapshot = {
            "state":     this.state,
            "isVisible": isVisibleLifecycleState( this.state ),
        };
    }

    /**
     * Subscribe to lifecycle transitions, attaching DOM listeners on first use.
     *
     * @param listener Called on every real state change.
     * @returns Unsubscribe function.
     */
    public subscribe( listener: PageLifecycleListener ): () => void {
        this.startListening();
        this.listeners.add( listener );

        return (): void => {
            this.listeners.delete( listener );
        };
    }

    /**
     * Read the current lifecycle state.
     *
     * @returns The current state.
     */
    public getState(): PageLifecycleState {
        return this.state;
    }

    /**
     * Read the current snapshot. The reference changes only on real transitions,
     * which keeps `useSyncExternalStore` from re-rendering on every event.
     *
     * @returns The cached snapshot.
     */
    public getSnapshot(): PageLifecycleSnapshot {
        return this.snapshot;
    }

    /**
     * Detach DOM listeners and drop subscribers.
     *
     * @remarks Only needed for tests and teardown; the application singleton
     * lives as long as the document.
     * @returns Nothing.
     */
    public stop(): void {
        if ( this.isListening ) {
            for ( const eventName of LIFECYCLE_EVENT_NAMES ) {
                this.target.removeEventListener( eventName, this.handleEvent, { "capture": true } );
            }
            this.isListening = false;
        }
        this.listeners.clear();
    }

    /**
     * Attach DOM listeners once, then resynchronize in case the state already
     * moved before the first subscription.
     *
     * @returns Nothing.
     */
    private startListening(): void {
        if ( this.isListening ) {
            return;
        }
        this.isListening = true;

        for ( const eventName of LIFECYCLE_EVENT_NAMES ) {
            this.target.addEventListener( eventName, this.handleEvent, { "capture": true } );
        }

        this.transitionTo( this.readCurrentState() );
    }

    /**
     * Derive the state from the DOM, used when an event does not imply a state
     * by itself (`resume`, `pageshow`, `visibilitychange`, `blur`).
     *
     * @returns `hidden`, `active`, or `passive`.
     */
    private readCurrentState(): PageLifecycleState {
        const { "document": targetDocument } = this.target;

        if ( targetDocument.visibilityState === "hidden" ) {
            return "hidden";
        }

        return targetDocument.hasFocus() ? "active" : "passive";
    }

    /**
     * Transition to whatever state the DOM currently reports.
     *
     * Used by events that signal "something changed" without implying a state
     * on their own: `resume`, `pageshow`, `blur`, and `visibilitychange`.
     *
     * @returns Nothing.
     */
    private syncStateFromDocument(): void {
        this.transitionTo( this.readCurrentState() );
    }

    /**
     * Map a lifecycle event to its resulting state.
     *
     * Bound as a property so the same reference is used for add and remove.
     *
     * @param event Lifecycle event captured on the target window.
     * @returns Nothing.
     */
    private readonly handleEvent = ( event: Event ): void => {
        switch ( event.type ) {
            case "focus":
                /*
                 * The event Chrome does deliver after a freeze/thaw cycle when
                 * `visibilitychange` is skipped. This is the missed-resume path.
                 */
                this.transitionTo( "active" );
                break;

            case "blur":
                /*
                 * `blur` also fires while unloading, so only refine a state that
                 * is still `active`; otherwise the DOM would report a stale value.
                 */
                if ( this.state === "active" ) {
                    this.syncStateFromDocument();
                }
                break;

            case "resume":
                // Emitted when the browser thaws a frozen page.
                this.syncStateFromDocument();
                break;

            case "pageshow":
                // Emitted on load and on back/forward cache restore.
                this.syncStateFromDocument();
                break;

            case "freeze":
                this.transitionTo( "frozen" );
                break;

            case "pagehide":
                this.transitionTo( ( event as PageTransitionEvent ).persisted ? "frozen" : "terminated" );
                break;

            case "visibilitychange":
                /*
                 * `visibilityState` flips to hidden during unload too, but the
                 * lifecycle state must not regress once frozen or terminated.
                 */
                if ( this.state !== "frozen" && this.state !== "terminated" ) {
                    this.syncStateFromDocument();
                }
                break;

            default:
                break;
        }
    };

    /**
     * Commit a state change and notify subscribers, ignoring no-op transitions.
     *
     * @param nextState State the page moves to.
     * @returns Nothing.
     */
    private transitionTo( nextState: PageLifecycleState ): void {
        if ( nextState === this.state ) {
            return;
        }

        const previousState = this.state;
        const wasVisible = isVisibleLifecycleState( previousState );
        const isVisible = isVisibleLifecycleState( nextState );

        this.state = nextState;
        this.snapshot = {
            "state": nextState,
            isVisible,
        };

        const transition: PageLifecycleTransition = {
            previousState,
            nextState,
            wasVisible,
            isVisible,
        };

        for ( const listener of this.listeners ) {
            listener( transition );
        }
    }

}

/**
 * Application-wide lifecycle store.
 *
 * @remarks DOM listeners are attached on the first {@link PageLifecycleStore.subscribe}
 * call, so importing this module has no side effect.
 */
export const pageLifecycleStore = new PageLifecycleStore();

/**
 * Subscribe to on-screen / off-screen flips only.
 *
 * Filters out transitions that keep visibility unchanged, so imperative
 * pause/resume paths are not disturbed by `active` <-> `passive` churn (the
 * user merely clicking outside the window) nor by `hidden` -> `frozen`.
 *
 * @param listener Called with the new visibility whenever it actually flips.
 * @param store Store to observe. Defaults to the application singleton.
 * @returns Unsubscribe function.
 */
export const subscribeToPageVisibilityTransitions = (
    listener: ( isVisible: boolean ) => void,
    store: PageLifecycleStore = pageLifecycleStore,
): () => void => {
    return store.subscribe( ( transition ) => {
        if ( transition.isVisible === transition.wasVisible ) {
            return;
        }

        listener( transition.isVisible );
    } );
};
